import {
  useQuery,
  useMutation,
  type QueryKey,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import queryString from 'query-string';
import {
  isEmpty,
  isUndefined,
  omitBy,
  isPlainObject,
  castArray,
  includes,
  reject,
  isArray,
  map,
  get,
  isEqual,
  filter,
} from 'lodash';

import { reportingAppealsApi } from '@xtnd/web/features/ar-collections/api';
import { PaginatedResponse, QueryStringParams } from '@xtnd/web/shared';
import {
  Appeal,
  APPEAL_SORT_BY,
  AppealGroup,
  UpdateAppealStatusPayload,
  AppealItem,
} from '@xtnd/web/features/ar-collections/types';
import { showToast } from '@repo/ui';
import { StableQueryKey, useQueryKeyStore } from '@xtnd/web/shared/stores';
import { AppealStatus } from '@repo/types';
import { AppealType } from '@repo/types';
import { getApiErrorMessage } from '../utils/apiErrorHandler';

/**
 * Custom hook to fetch appeals data with pagination, filters, and sorting.
 *
 * @param params - Object containing query parameters
 * @param params.page - Current page number (default: 1)
 * @param params.limit - Number of items per page (default: 50)
 * @param params.filters - Filters to apply to the query
 * @param params.sorts - Sorting options
 * @param options - React Query options
 * @param options.enabled - Whether the query should be enabled (default: true)
 *
 * @returns A React Query result object containing the appeals data and query status
 */
export function useAppeals(params: QueryStringParams, options?: { enabled?: boolean }) {
  const { setQueryKey, getQueryKey } = useQueryKeyStore();

  // Set default query params
  const queryParams: Record<string, any> = {
    page: params.page || 1,
    limit: params.limit || 50,
    filters: params.filters || {},
  };

  const sorts = params.sorts as Record<APPEAL_SORT_BY, 'asc' | 'desc'>;

  // sorting by 1 field
  if (sorts) {
    const values = (Object.entries(sorts) as [APPEAL_SORT_BY, 'asc' | 'desc' | undefined][]).find(
      ([, order]) => order !== undefined
    );

    if (values) {
      const [sortBy, order] = values;
      queryParams.sortBy = sortBy;
      queryParams.order = order;
    }
  }

  // Remove undefined values and empty objects
  const cleanedParams = omitBy(
    queryParams,
    value => isUndefined(value) || (isPlainObject(value) && isEmpty(value))
  );

  // Build a stable query key
  const queryKey = [
    'appeals',
    queryString.stringify(
      {
        ...cleanedParams,
        filters: JSON.stringify(cleanedParams.filters),
        sorts: JSON.stringify({ sortBy: queryParams.sortBy, order: queryParams.order }),
      },
      { arrayFormat: 'comma' }
    ),
  ];

  const queryKeyStore = getQueryKey('ar-collections', 'reporting.appeal');
  if (!isEqual(queryKeyStore, queryKey)) {
    setQueryKey('ar-collections', 'reporting.appeal', queryKey);
  }

  return useQuery({
    queryKey,
    queryFn: () => reportingAppealsApi.getAppeals(cleanedParams),
    enabled: options?.enabled ?? true,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });
}

/**
 * Custom hook for updating appeal status with optimistic updates and intelligent cache management
 *
 * This hook provides a mutation that updates an appeal's status, deadline, notes, and approved amount.
 * It implements optimistic updates for immediate UI feedback and handles cache synchronization
 * for both client-grouped and non-grouped data structures.
 *
 * @param queryParams - Query parameters used to build the query key for cache invalidation.
 *                      Undefined or empty object values will be removed before building the key.
 *                      The `filters.groupBy` value determines how cache updates are handled.
 *
 * @returns A React Query mutation object containing:
 *   - `mutate`: Function to trigger the mutation
 *   - `mutateAsync`: Async version of mutate that returns a promise
 *   - `isLoading`: Boolean indicating if mutation is in progress
 *   - `error`: Error object if mutation failed
 *   - `data`: Response data if mutation succeeded
 *
 * @remarks
 * **Optimistic Updates:**
 * - Updates the cache immediately before the API call completes
 * - Handles both simple arrays and paginated responses
 * - Supports client-grouped data structures
 *
 * **Cache Management:**
 * - When `groupBy === 'client'`: Updates both the main appeals cache and the `appealsSelected` cache
 * - When `groupBy !== 'client'`: Updates the main appeals cache only
 * - Automatically removes appeals from the list if the new status doesn't match current filters
 * - Adjusts pagination totals when appeals are removed
 *
 * **Error Handling:**
 * - Automatically rolls back optimistic updates on error
 * - Shows error toast notification with error message
 *
 * **Success Handling:**
 * - Updates cache with server response data
 * - Shows success toast notification
 * - Handles status filter matching to determine if appeal should remain visible
 *
 * @example
 * ```typescript
 * Basic usage
 * const { mutate: updateStatus, isLoading } = useUpdateAppealStatus({
 *   page: 1,
 *   limit: 25,
 *   filters: { groupBy: 'client', status: ['APPEAL_SUBMITTED'] }
 * });
 *
 * Update appeal status
 * updateStatus({
 *   invoiceId: 'invoice-123',
 *   nextStatus: 'APPEAL_APPROVED',
 *   deadline: '2024-12-31',
 *   notes: 'Approved with conditions',
 *   approvedAmount: 5000
 * });
 * ```
 */
export function useUpdateAppealStatus(queryParams: QueryStringParams, isAppealSelected = false) {
  const queryClient = useQueryClient();
  const { getQueryKey } = useQueryKeyStore();

  const writeOffQueryKey = getQueryKey('ar-collections', 'reporting.write-off');
  const matterInvoicesQueryKey = getQueryKey('ar-collections', 'queue.matter-invoices');
  const emailQueueQueryKey = getQueryKey('ar-collections', 'reporting.email-queue');
  const appealByClientIdQueryKey = getQueryKey('ar-collections', 'reporting.appeal-by-client-id');
  const appealsQueryKey = getQueryKey('ar-collections', 'reporting.appeal');

  const invalidateQueryKeys = filter(
    [writeOffQueryKey, matterInvoicesQueryKey, emailQueueQueryKey],
    query => !isEmpty(query)
  ) as StableQueryKey[];

  const _applyUpdateToCache = (cache: any, updater: (appeal: Appeal) => Appeal) => {
    if (!cache) return cache;

    if (Array.isArray(cache)) {
      return cache.map(updater);
    }

    if (cache.data && Array.isArray(cache.data)) {
      return {
        ...cache,
        data: cache.data.map(updater),
      };
    }

    return cache;
  };

  return useMutation<
    { data: Appeal },
    Error,
    UpdateAppealStatusPayload,
    { previousData: unknown; queryKey: QueryKey }
  >({
    mutationFn: (payload: UpdateAppealStatusPayload) =>
      reportingAppealsApi.updateAppealStatus(payload),

    // Optimistic update
    onMutate: async payload => {
      const queryKey = appealsQueryKey;

      // Cancel outgoing queries
      await queryClient.cancelQueries({ queryKey });
      // Snapshot previous value
      const previousData = queryClient.getQueryData(queryKey);

      if (isAppealSelected) {
        const appealByClientData = queryClient.getQueryData(appealByClientIdQueryKey) as
          | PaginatedResponse<Appeal>
          | undefined;
        const dataArray = appealByClientData?.data;

        if (appealByClientData && Array.isArray(dataArray)) {
          const invoiceIndex = dataArray.findIndex(
            (invoice: Appeal) => invoice.invoiceId === payload.invoiceId
          );

          if (invoiceIndex !== -1) {
            const invoice = dataArray[invoiceIndex] as Appeal;
            const appealType = payload.appealType;
            let status = payload.nextStatus;
            if (appealType === AppealType.BACKLOG) {
              status = AppealStatus.APPEAL_SUBMITTED;
            }

            const updatedInvoice = {
              ...invoice,
              status,
              deadline: payload.deadline ? payload.deadline : invoice?.deadline,
              collectedAmount: payload.collectedAmount ?? invoice.collectedAmount,
            } as Appeal;
            dataArray[invoiceIndex] = updatedInvoice;
          }

          queryClient.setQueryData(appealByClientIdQueryKey, appealByClientData);
        }

        // Return the previous data and query key for the appeals client group
        return {
          previousData: appealByClientData,
          queryKey,
        };
      }

      // Update non-client group data
      // Optimistically update
      queryClient.setQueryData(queryKey, (oldData: any) =>
        _applyUpdateToCache(oldData, appeal => {
          if (appeal.invoiceId !== payload.invoiceId) {
            return appeal;
          }

          const appealType = payload.appealType;
          let status = payload.nextStatus;
          if (appealType === AppealType.BACKLOG) {
            status = AppealStatus.APPEAL_SUBMITTED;
          }

          return {
            ...appeal,
            status,
            deadline: payload.deadline ? payload.deadline : appeal.deadline,
            collectedAmount: payload.collectedAmount ?? appeal.collectedAmount,
          };
        })
      );

      return { previousData, queryKey };
    },

    onSuccess: (response, _payload, context) => {
      const updatedAppeal = response?.data;
      if (!updatedAppeal || !context?.queryKey) return;

      invalidateQueryKeys.forEach(queryKey => {
        queryClient.invalidateQueries({ queryKey });
      });

      const statusFilters = castArray(queryParams.filters?.status).filter(Boolean);
      const isStatusInFilter =
        isEmpty(statusFilters) || includes(statusFilters, updatedAppeal.status);

      const removeAppeal = (data: Appeal[]) => reject(data, { invoiceId: updatedAppeal.invoiceId });

      const updateAppeal = (data: Appeal[]) =>
        map(data, appeal =>
          appeal.invoiceId === updatedAppeal.invoiceId ? updatedAppeal : appeal
        );

      if (isAppealSelected) {
        // Invalidate the appeals query key to get the latest data
        queryClient.invalidateQueries({ queryKey: appealsQueryKey });

        // Update the appeal by client id query key to get the latest data
        queryClient.setQueryData(appealByClientIdQueryKey, (oldData: any) => {
          if (!oldData) return oldData;

          // Simple array
          if (isArray(oldData)) {
            return isStatusInFilter ? updateAppeal(oldData) : removeAppeal(oldData);
          }

          // Paginated data
          if (get(oldData, 'data')) {
            const newData = isStatusInFilter
              ? updateAppeal(oldData.data)
              : removeAppeal(oldData.data);

            return {
              ...oldData,
              data: newData,
              pagination: {
                ...oldData.pagination,
                total: isStatusInFilter
                  ? oldData.pagination?.total
                  : Math.max((oldData.pagination?.total ?? 1) - 1, 0),
              },
            };
          }

          return oldData;
        });
        showToast.success('The appeal status was updated successfully.');
        return;
      }

      queryClient.setQueryData(context.queryKey, (oldData: any) => {
        if (!oldData) return oldData;

        // Simple array
        if (isArray(oldData)) {
          return isStatusInFilter ? updateAppeal(oldData) : removeAppeal(oldData);
        }

        // Paginated data
        if (get(oldData, 'data')) {
          const newData = isStatusInFilter
            ? updateAppeal(oldData.data)
            : removeAppeal(oldData.data);

          return {
            ...oldData,
            data: newData,
            pagination: {
              ...oldData.pagination,
              total: isStatusInFilter
                ? oldData.pagination?.total
                : Math.max((oldData.pagination?.total ?? 1) - 1, 0),
            },
          };
        }

        return oldData;
      });

      showToast.success('The appeal status was updated successfully.');
    },

    onError: (error, _payload, context) => {
      // Rollback if error
      if (context?.previousData && context?.queryKey) {
        queryClient.setQueryData(context.queryKey, context.previousData);
      }
      showToast.error(
        getApiErrorMessage(error, 'Failed to update appeal status. Please try again.')
      );
    },
  });
}

/**
 * Custom hook to fetch appeal history for a specific invoice.
 *
 * @param invoiceId - The ID of the invoice to fetch appeal history for
 * @param options - React Query options
 * @param options.enabled - Whether the query should be enabled (default: true)
 *
 * @returns A React Query result object containing the appeal history data and query status
 */
export function useAppealHistory(invoiceId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['appeal-history', invoiceId],
    queryFn: () => reportingAppealsApi.getAppealHistory(invoiceId),
    enabled: options?.enabled ?? true,
    staleTime: 0,
    gcTime: 0,
  });
}

/**
 * Custom hook to fetch appeal history for a specific invoice.
 *
 * @param invoiceId - The ID of the invoice to fetch appeal history for
 * @param options - React Query options
 * @param options.enabled - Whether the query should be enabled (default: true)
 *
 * @returns A React Query result object containing the appeal history data and query status
 */
export function useGetAppealsSelected(options?: { enabled?: boolean }) {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: ['appealsSelected'],
    queryFn: () => {
      return queryClient.getQueryData<AppealGroup>(['appealsSelected']);
    },
    enabled: options?.enabled ?? true,
    staleTime: Infinity,
  });
}

export function useGetAppealsByGroupId(
  groupId: string,
  params: QueryStringParams,
  options?: { enabled?: boolean }
) {
  // Set default query params
  const queryParams: Record<string, any> = {
    page: params.page || 1,
    limit: params.limit || 50,
    filters: params.filters || {},
  };

  const sorts = params.sorts as Record<APPEAL_SORT_BY, 'asc' | 'desc'>;

  // sorting by 1 field
  if (sorts) {
    const values = (Object.entries(sorts) as [APPEAL_SORT_BY, 'asc' | 'desc' | undefined][]).find(
      ([, order]) => order !== undefined
    );

    if (values) {
      const [sortBy, order] = values;
      queryParams.sortBy = sortBy;
      queryParams.order = order;
    }
  }

  const cleanedParams: Record<string, any> = omitBy(
    queryParams,
    value => isUndefined(value) || (isPlainObject(value) && isEmpty(value))
  );

  const query = queryString.stringify(
    {
      ...cleanedParams,
      filters: cleanedParams.filters ? JSON.stringify(cleanedParams.filters) : undefined,
    },
    { arrayFormat: 'comma' }
  );

  const queryKey = ['appealsByGroupId', groupId, query];
  const { getQueryKey, setQueryKey } = useQueryKeyStore();
  const appealsByGroupIdQueryKey = getQueryKey('ar-collections', 'reporting.appeal-by-group-id');
  if (!isEqual(appealsByGroupIdQueryKey, queryKey)) {
    setQueryKey('ar-collections', 'reporting.appeal-by-group-id', queryKey);
  }

  return useQuery({
    queryKey: ['appealsByGroupId', groupId, query],
    queryFn: () => reportingAppealsApi.getAppealsByGroupId(groupId, query),
    enabled: options?.enabled ?? true,
    staleTime: 0,
    gcTime: 0,
    retry: 2,
  });
}

export function useGetAppealList(invoiceId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['appeals', 'invoices', invoiceId],
    queryFn: () => reportingAppealsApi.getAppealList(invoiceId),
    enabled: options?.enabled ?? false,
    staleTime: 0,
    gcTime: 0,
    retry: 1,
  });
}

/**
 * Utility function to update appeal deadlines in the appealsQueryKey cache without calling the API.
 * This is useful for optimistic updates or when you want to update the cache directly.
 *
 * @param queryClient - React Query client instance
 * @param appealsQueryKey - The query key for the appeals cache
 * @param invoiceId - The invoice ID to update
 * @param payload - The deadline values to update
 * @param payload.deadline - The new deadline value (optional)
 * @param payload.attyDeadline - The new attyDeadline value (optional)
 */

export function updateAppealsCacheDeadlines(
  queryClient: QueryClient,
  appealsQueryKey: QueryKey,
  invoiceId: string,
  payload: { deadline?: string | null; attyDeadline?: string | null }
) {
  queryClient.setQueryData<PaginatedResponse<Appeal>>(appealsQueryKey, old => {
    if (!old) return old;

    return {
      ...old,
      data: old.data.map(item => {
        if (item.invoiceId !== invoiceId) {
          return item;
        }
        const updatedItem: Appeal = {
          ...item,
          deadline: payload.deadline ?? undefined,
          attyDeadline: payload.attyDeadline ?? undefined,
        };

        return updatedItem;
      }),
    };
  });
}

export function useUpdateAppealDeadlines(invoiceId: string, appealId: string) {
  const queryClient = useQueryClient();
  const { getQueryKey } = useQueryKeyStore();
  const appealsQueryKey = getQueryKey('ar-collections', 'reporting.appeal');
  const appealListQueryKey: QueryKey = ['appeals', 'invoices', invoiceId];

  return useMutation({
    mutationKey: ['update-appeal-deadlines', invoiceId, appealId],

    mutationFn: (payload: { deadline?: string | null; attyDeadline?: string | null }) =>
      reportingAppealsApi.updateAppealDeadlines({
        invoiceId,
        appealId,
        ...payload,
      }),

    onSuccess: response => {
      const updatedAppeal = response.data;

      queryClient.setQueryData<AppealItem[]>(appealListQueryKey, old => {
        if (!old) return old;

        return old.map(a =>
          a.appealId === appealId
            ? {
                ...a,
                deadline: updatedAppeal.deadline,
                attyDeadline: updatedAppeal.attyDeadline,
              }
            : a
        );
      });

      // Use the utility function to update the appeals cache
      updateAppealsCacheDeadlines(queryClient, appealsQueryKey, invoiceId, {
        deadline: updatedAppeal.deadline,
        attyDeadline: updatedAppeal.attyDeadline,
      });
    },

    onError: (error: any) => {
      showToast.error(
        getApiErrorMessage(error, 'Failed to update appeal deadlines. Please try again.')
      );
    },
  });
}
